<template>
  <div :key="'evaluation-' + evaluationId" class="evaluation-chart">
    <evaluation-data-sidebar
      v-if="dataEditable"
      :evaluation-id="evaluationId"
      class="evaluation-chart__sidebar"
    />
    <div class="evaluation-chart__main">
      <div
        ref="bodyElement"
        class="evaluation-chart__body"
        :class="{
          '-fitting': settings.width == null && settings.height == null
        }"
      >
        <Chart
          :chart-item="evaluation"
          :width="settings.width ?? bodyWidth"
          :height="settings.height ?? bodyHeight"
          :with-legend="true"
          :title="evaluation.title"
          :title-clickable="titleClickable"
          :editable="dataEditable"
          :override-settings="overrideSettings"
          class="evaluation-chart__chart"
          :class="{
            '-fitting': settings.width == null && settings.height == null
          }"
          :margin="{ top: 20, left: 20, bottom: 20, right: 20 }"
          @open-round-actions="updateInspectedRoundActions($event)"
          @click:title="$emit('click:title', $event)"
          @click:axis-title="openAxisPopup($event)"
          @click:column-title="updateShortTitle($event)"
          @update:short-title="updateShortTitle($event)"
          @update:title="updateTitle($event)"
        >
          <template #empty>
            <div
              v-if="dataEditable == false"
              class="evaluation-chart__errorview"
            >
              <!-- ERROR: NO DATA and NOT EDITABLE -->
              <h2>{{ $t("headings.noEvaluationData") }}</h2>
              <p class="message">
                {{ $t("errors.noEvaluationDataSelected") }}
              </p>
              <button
                type="button"
                class="button"
                @click="$emit('click:title', $event)"
              >
                {{ $t("actions.openChart") }}
              </button>
            </div>

            <div
              v-else-if="ctx.chartAxisConfig == null"
              class="evaluation-chart__startview"
            >
              <!-- ERROR: NO CHART TYPE -->
              <slot name="no-diagram">
                <evaluation-chart-suggestions :evaluation-id="evaluationId" />
              </slot>
            </div>

            <div
              v-else-if="ctx.orderedAvailableItems.simulation.length == 0"
              class="evaluation-chart__errorview"
            >
              <!-- NO SIMULATIONS -->
              <h2>{{ $t("headings.noSimulationsCreated") }}</h2>
              <p class="message">
                {{ $t("errors.noSimulations") }}
              </p>

              <router-link to="simulation" class="button">
                {{ $t("links.toSimulationArea") }}
              </router-link>
            </div>

            <div
              v-else-if="ctx.selectedSimulations.length == 0"
              class="evaluation-chart__startview"
            >
              <!-- NO SIMULATIONS SELECTED -->
              <h4 class="label">
                {{
                  $t(
                    "objectNames.simulation",
                    ctx.orderedAvailableItems.simulation.length
                  )
                }}
              </h4>
              <div class="grid-auto" style="--max-item-width: 25rem">
                <button
                  v-for="simulation in ctx.orderedAvailableItems.simulation"
                  :key="simulation.id"
                  type="button"
                  class="evaluation-chart__simulationoption"
                  :data-simulation-id="simulation.id"
                  @click="addSimulation(evaluationId, simulation.id)"
                >
                  <div class="mb-2">
                    <strong>{{ simulation.title }}</strong>
                  </div>
                  <div>{{ formatDateString(simulation.createdAt) }}</div>
                  <div v-if="simulation.scenarioId != null">
                    <span>{{ $t("objectNames.scenario") }}: </span>
                    <store-item
                      :id="simulation.scenarioId ?? undefined"
                      type="scenario"
                    ></store-item>
                  </div>
                </button>
              </div>
            </div>

            <div
              v-else-if="ctx.selectedItems.length == 0"
              class="evaluation-chart__errorview"
            >
              <h2>{{ $t("headings.chooseData") }}</h2>
              <p v-if="ctx.chartAxisConfig?.singleItem" class="message">
                <i18n-t keypath="texts.chooseChartDataSingle" scope="global">
                  <template #type>
                    {{
                      orListJoiner.format(
                        ctx.chartAxisConfig.item.map(
                          (type) => '"' + $t(`objectNames.${type}`) + '"'
                        )
                      )
                    }}
                  </template>
                </i18n-t>
              </p>

              <p v-else class="message">
                <i18n-t keypath="texts.chooseChartDataMultiple" scope="global">
                  <template #types>
                    {{
                      orListJoiner.format(
                        ctx.chartAxisConfig.item.map((type) =>
                          $t(`objectNames.${type}`, 3)
                        )
                      )
                    }}
                  </template>
                </i18n-t>
              </p>
            </div>

            <div
              v-else-if="ctx.allRoundOptions.length == 0"
              class="evaluation-chart__errorview"
            >
              <h2>{{ $t("headings.noRoundsSimulated") }}</h2>
              <p class="message">
                {{ $t("errors.noEvaluationRounds") }}
              </p>
            </div>

            <div v-else class="evaluation-chart__errorview">
              <p class="message p-4">
                {{ $t("errors.noEvaluationData") }}
              </p>
            </div>
          </template>
        </Chart>


        <!-- <template
          v-if="
            collectedChartData.data.length > 0 &&
            bodyWidth > 0 &&
            bodyHeight > 0
          "
        >
        </template> -->
      </div>
    </div>

    <button
      v-if="size(settings) > 3 || $slots.settings"
      class="button-icon evaluation-chart__settings"
      type="button"
      :title="$t('labels.settings')"
      @click="toggleChartSettings"
    >
      <lucide-icon name="settings" />
    </button>

    <floating-element
      v-if="settingsMenu.visible"
      class="evaluation-chart__settingspopup"
      transition-name="transition-from-top-right"
      x-alignment="right"
      :x="settingsMenu.x"
      :x-offset="settingsMenu.xOffset"
      :y="settingsMenu.y"
      @pointerdown:outside="settingsMenu.visible = false"
    >
      <slot
        name="settings"
        :settings="settings"
        :body-width="bodyWidth"
        :body-height="bodyHeight"
      >
        <chart-settings-list
          :chart="evaluation"
          :settings="settings"
          :body-width="bodyWidth"
          :body-height="bodyHeight"
          @update="updateChartSettings($event)"
        />
      </slot>
    </floating-element>

    <context-menu
      :visible="axisChooser.axis != null"
      :x="axisChooser.x"
      :x-offset="axisChooser.xOffset"
      :y="axisChooser.y"
      @close="axisChooser.axis = null"
    >
      <button
        v-for="option in axisChooser.axis == 'x' ? xAxisOptions : yAxisOptions"
        :key="option"
        type="button"
        class="context-menu__entry"
        @click="setAxis(evaluationId, axisChooser.axis, option)"
      >
        {{ $t(`chartAxes.${option}`) }}
      </button>
    </context-menu>

    <round-actions-popover
      class="evaluation-chart__roundpopover"
      :visible="inspectedRoundAction.visible"
      :x="inspectedRoundAction.x"
      :y="inspectedRoundAction.y"
      :simulation-ids="inspectedRoundAction.simulationIds"
      :simulation-id="inspectedRoundAction.simulationId"
      :round-index="inspectedRoundAction.roundIndex"
      :impulse-source="inspectedRoundAction.impulseSource"
      :max-height="bodyHeight - 150"
      @update:impulse-source="inspectedRoundAction.impulseSource = $event"
      @update:simulation-id="inspectedRoundAction.simulationId = $event"
      @close="inspectedRoundAction.visible = false"
    />

    <chart-image-export-popup
      :visible="imageExport.visible"
      :evaluation-id="evaluationId"
      :x="imageExport.x"
      :y="imageExport.y"
      @close="imageExport.visible = false"
    />
  </div>
</template>

<script setup lang="ts">
import { reactive, ref, computed, watch } from "vue";
import { useElementSize } from "@vueuse/core";
import { useFloor } from "@vueuse/math";
import { useI18n } from "vue-i18n";

import { showPrompt } from "@core/ui/alert/alert";
import { formatDate, orListJoiner } from "@/i18n";

import LucideIcon from "@core/ui/LucideIcon.vue";
import FloatingElement from "@core/ui/FloatingElement.vue";

import RoundActionsPopover from "@editor/items/simulationRound/RoundActionsPopover.vue";

import StoreItem from "../StoreItem.vue";
import ContextMenu from "@core/ui/ContextMenu.vue";
import ChartImageExportPopup from "./ChartImageExportPopup.vue";
import EvaluationDataSidebar from "./EvaluationDataSidebar.vue";
import ChartSettingsList from "@editor/items/charts/ChartSettingsList.vue";
import EvaluationChartSuggestions from "./EvaluationChartSuggestions.vue";
import Chart from "@editor/items/charts/Chart.vue";

import { useEvaluationStore } from "./evaluations.store";
import { useReactiveChartData } from "../charts/useChartData.js";
import { useSettingsStore } from "@editor/store/settings.store";
import { useSimulationStore } from "@editor/items/simulation/simulations.store";
import { useModelStore } from "../model/model.store";

import { getElementOrAncestorWithTag } from "@/helpers/utility/DOM";

import type { AlertContentOption } from "@core/ui/alert/alert";
import type { PreSettings } from "@editor/items/charts/charts.js";
import type { EntryValue } from "@editor/items/charts/ChartData.js";
import type { ChartSettings } from "@editor/items/charts/ChartItem.js";

interface Props {
  evaluationId: number;
  dataEditable?: boolean;
  overrideSettings?: Partial<PreSettings>;
  titleClickable?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  dataEditable: false,
  overrideSettings: () => ({}),
  titleClickable: false
});

defineEmits<{
  (e: "click:title", event: MouseEvent): void;
  (e: "update:short-title", data: { uid: string; title: string }): void;
  (e: "close"): void;
}>();

const { t } = useI18n();
const evaluationStore = useEvaluationStore();
const { setAxis, addSimulation } = evaluationStore;

const evaluation = computed(() => {
  return evaluationStore.items[props.evaluationId];
});

const ctx = useReactiveChartData(evaluation);

const bodyElement = ref<HTMLElement>();
const { width: _bodyWidth, height: _bodyHeight } = useElementSize(bodyElement);
const bodyWidth = useFloor(_bodyWidth);
const bodyHeight = useFloor(_bodyHeight);
watch(bodyHeight, (v) => console.log("bodyHeight", v));

function updateTitle(title: string | null) {
  evaluationStore.updateKey(props.evaluationId, "title", title ?? "");
}

// ==============
// SETTINGS

const scaleColors = computed(() => {
  const colors = useSettingsStore().targetValueColors.slice();
  if (colors == null) return [];
  return colors;
});

const settings = computed(() => {
  return {
    ...ctx.availableSettingsValues,
    scaleColors: scaleColors.value,
    ...ctx.preSettings,
    ...props.overrideSettings
  };
});

const settingsMenu = reactive({
  visible: false,
  x: 0,
  y: 0,
  xOffset: 20
});

function toggleChartSettings(event: MouseEvent) {
  const button = getElementOrAncestorWithTag(
    event.target as HTMLElement,
    "BUTTON"
  );
  if (button != null) {
    const rect = button.getBoundingClientRect();
    settingsMenu.x = rect.left + rect.width / 2;
    settingsMenu.y = rect.top;
    settingsMenu.xOffset = rect.width / 2;
  } else {
    settingsMenu.x = event.clientX;
    settingsMenu.y = event.clientY;
  }
  settingsMenu.visible = !settingsMenu.visible;
}

function updateChartSettings(settings: Partial<ChartSettings>) {
  return evaluationStore.updateChartSettings(props.evaluationId, settings);
}

// ==============
// LEGEND

/**
 * Change the short title of an item.
 * Called from the chart legend popup.
 */
async function updateShortTitle(entry: {
  title: string;
  shortTitle?: string;
  groupTitle?: string;
  groupShortTitle?: string;
  uid: string;
  type: string; // "resource" | "simulation" | "target" | "index";
  id: number | string;
  withSimulation?: boolean;
}) {
  const uids = entry.uid.split(".");
  const uid = uids[uids.length - 1];

  const simulationStore = useSimulationStore();
  const modelStore = useModelStore();

  const entryType = entry.type as "resource" | "simulation" | "target" | "index";
  const item = modelStore.getItem(entryType, Number(entry.id));

  const promptInputs: AlertContentOption[] = [
    {
      type: "input",
      label: t("labels.shortTitle") + " " + t(`objectNames.${entry.type}`),
      value: entry.shortTitle ?? "",
      placeholder: item?.title ?? "",
      name: "shortTitle",
      autofocus: true
    },
    {
      type: "text",
      text: entry.title,
      class: "mb-2 mt--1 color-text-medium weight-normal pl-3"
    }
  ];
  if (entry.groupTitle && uids.length > 1) {
    promptInputs.push(
      {
        type: "input",
        label: t("labels.shortTitle") + " " + t("objectNames.simulation"),
        value: entry.groupShortTitle ?? "",
        placeholder: simulationStore.byUid[uids[0]]?.title ?? "",
        name: "simulationShortTitle"
      },
      {
        type: "text",
        text: entry.groupTitle ?? "",
        class: "mb-2 mt--1 color-text-medium weight-normal pl-3"
      }
    );
  }

  const titlePrompt = await showPrompt<{ shortTitle: string, simulationShortTitle: string }>({
    title: t("actions.assignAbbreviation"),
    content: promptInputs
  });
  if (!titlePrompt) return;
  
  const { shortTitle, simulationShortTitle } = titlePrompt;
  if (shortTitle != null) {
    evaluationStore.updateTitleAbbreviation(uid, shortTitle.trim());
  }
  if (entry.withSimulation && simulationShortTitle != null) {
    evaluationStore.updateTitleAbbreviation(
      uids[0],
      simulationShortTitle.trim()
    );
  }
}

// ==================
// ROUND ACTIONS BAR

const inspectedRoundAction = reactive({
  visible: false,
  simulationIds: [] as number[],
  simulationId: null as number | null,
  roundIndex: 0,
  impulseSource: null as
    | null
    | { type: "action"; id: number }
    | { type: "incident"; id: number }
    | { type: "scheduledEffect"; id: string },
  x: 0,
  y: 0
});

function updateInspectedRoundActions(data?: {
  position: {
    x?: number;
    y?: number;
  };
  entry: EntryValue;
}) {
  if (!data || data.entry.roundIndex == null) {
    inspectedRoundAction.visible = false;
    return;
  }

  inspectedRoundAction.visible = true;
  inspectedRoundAction.impulseSource = null;
  inspectedRoundAction.simulationId = null;

  inspectedRoundAction.x = data.position.x || 0;
  inspectedRoundAction.y = data.position.y || 0;

  inspectedRoundAction.simulationId = data.entry.simulationId ?? null;
  inspectedRoundAction.roundIndex = data.entry.roundIndex;
}

// ==============
// AXIS CHOOSER

const axisChooser = reactive({
  axis: null as null | "x" | "y",
  x: 0,
  y: 0,
  xOffset: 20
});

function openAxisPopup(event: { x: number; y: number; axis: "x" | "y" }) {
  if (!props.dataEditable) return;
  axisChooser.axis = event.axis;
  axisChooser.x = event.x;
  axisChooser.y = event.y;
}

const xAxisOptions = computed(() => {
  if (!ctx.chartConfig) return [];
  const s = new Set(ctx.chartConfig.axisOptions.map((o) => o?.x));
  return Array.from(s).filter((i) => i != null);
});

const yAxisOptions = computed(() => {
  if (!ctx.chartConfig) return [];
  const s = new Set(ctx.chartConfig.axisOptions.map((o) => o?.y));
  return Array.from(s).filter((i) => i != null);
});

// ==============
// IMAGE EXPORT

const imageExport = reactive({
  visible: false,
  x: null as null | number,
  y: null as null | number
});

function openImageExportPopup() {
  if (Date.now() > 10) {
    imageExport.visible = true;
    return;
  }
}

// ==============
// Utilities

function formatDateString(timeStamp: string | number) {
  const date = new Date(timeStamp);
  return formatDate(date, "f");
}
function size(obj: object) {
  return Object.keys(obj).length;
}

defineExpose({
  openImageExportPopup
});
</script>

<style lang="scss">
.evaluation-chart {
  background-color: var(--color-background-deep);

  height: 100%;
  display: flex;
  position: relative;
  min-height: 2rem;

  &__sidebar {
    flex-shrink: 0;
    width: var(--sidebar-width);
    z-index: 1;
  }

  &__main {
    overflow: auto;
    height: 100%;
    width: 100%;
    display: grid;
    grid-template-rows: minmax(0, 1fr);
  }

  &__body {
    height: 100%;
    width: 100%;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: auto;

    @supports (-moz-appearance: none) {
      // only firefox supports this fully
      // and it interferes with colors in chrome
      scrollbar-width: thin;
    }

    &.-fitting {
      overflow: hidden;
    }
  }

  &__errorview {
    margin: auto;
    max-width: 32rem;
    text-align: center;
    padding: 1rem;
  }
  &__startview {
    padding: 1rem;
    height: 100%;
    width: 100%;
    overflow: auto;
  }

  &__settingspopup {
    padding: 0.5rem;
    background-color: var(--color-background-deep);
    box-shadow: 0 2px 4px var(--color-shadow-1);
    border-radius: 0.5rem;
    max-height: 100vh;
    max-height: 100svh;
    overflow: auto;
  }

  &__chart {
    contain: strict;
    // display: block;
    isolation: isolate;
    background-color: var(--color-background-deep);
    font-family: var(--font-family-ui);
    flex-shrink: 0;

    &:not(.-fitting) {
      outline: 1px solid var(--color-grey-light);
      outline-offset: -1px;
    }
  }

  &__popover {
    position: absolute;
    background-color: #fff;
    padding: 1rem;
  }

  &__simulationoption,
  &__typeoption {
    border: none;
    box-shadow: 0 2px 5px var(--color-shadow-2);
    border-radius: 5px;
    background-color: var(--color-background-close);
    padding: 1rem;

    &:hover {
      background-color: var(--color-background-close-hover);
    }
  }
  &__typeicon {
    font-size: 1.25rem;
    margin-bottom: -2px;
  }
  &__simulationoption {
    text-align: left;
  }

  &__settings,
  &__legendbutton {
    position: absolute;
    z-index: 1;
    top: 5px;
  }
  &__settings {
    right: 5px;
  }
  &__legendbutton {
    right: 35px;
  }

  &__notice {
    fill: var(--color-text-light);
    font-size: 12px;
    display: none;
    .-print & {
      display: inherit;
    }
  }
}
</style>
